import React from 'react';
import { Modal, Table, Alert, Button } from 'react-bootstrap';
import AppBadge from 'shared/badges/app-badge';

const STATUS_VARIANTS = {
  running: 'warning',
  success: 'success',
  error: 'danger',
  timeout: 'timeout'
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const formatSeconds = (seconds) => {
  if (seconds == null) return '—';
  const n = Number(seconds);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(1)}s`;
};

const formatNumber = (n) => {
  if (n == null) return '—';
  return Number(n).toLocaleString();
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const StatusBadge = ({ status }) => (
  <AppBadge variant={STATUS_VARIANTS[status] || 'secondary'}>
    {capitalize(status)}
  </AppBadge>
);

const AnalyticStatusBadge = ({ status }) => {
  if (status === 'success') return <AppBadge variant="success">Success</AppBadge>;
  if (status === 'error') return <AppBadge variant="danger">Error</AppBadge>;
  if (status === 'missing_data') return <AppBadge variant="secondary">Missing data</AppBadge>;
  return <AppBadge variant="secondary">{capitalize(status) || '—'}</AppBadge>;
};

// Renders the full per-row + per-step + per-analytic performance payload
// recorded by lambdas and importers that have adopted the new response shape.
// `rows` is an importer's account of what it did with its source rows, in the
// order they dropped out. Falls back to a friendly note when an older-style
// run's row has no response_payload so admins don't see an empty modal.
const PerformancePayloadModal = ({ show, onHide, dataUpdate }) => {
  if (!dataUpdate) return null;

  const data = dataUpdate.response_payload;
  const status = dataUpdate.status_text || dataUpdate.status;
  const isTerminated = status === 'timeout' || status === 'error';
  // For lambdas, client wall includes invocation overhead on top of the
  // lambda-reported wall; importers measure in-process so the two match and
  // the comparison would be noise.
  const wallTimesDiffer = data?.wall_seconds != null
    && dataUpdate.execution_seconds != null
    && Math.abs(Number(dataUpdate.execution_seconds) - Number(data.wall_seconds)) >= 0.1;
  const stepsHaveStatus = data?.steps?.some((step) => step.status != null);

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <code>{dataUpdate.function_name}</code>
          <StatusBadge status={status} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <dl className="row mb-3">
          <dt className="col-sm-3">Executed at</dt>
          <dd className="col-sm-9">{formatDate(dataUpdate.executed_at)}</dd>

          <dt className="col-sm-3">Wall time</dt>
          <dd className="col-sm-9">
            {formatSeconds(data?.wall_seconds ?? dataUpdate.execution_seconds)}
            {wallTimesDiffer && (
              <small className="text-muted ms-2">
                (lambda wall {formatSeconds(data.wall_seconds)} · client wall {formatSeconds(dataUpdate.execution_seconds)})
              </small>
            )}
          </dd>

          {data?.rows_upserted != null && (
            <>
              <dt className="col-sm-3">Rows upserted</dt>
              <dd className="col-sm-9">{formatNumber(data.rows_upserted)}</dd>
            </>
          )}

          {dataUpdate.message && (
            <>
              <dt className="col-sm-3">Message</dt>
              <dd className="col-sm-9">{dataUpdate.message}</dd>
            </>
          )}
        </dl>

        {isTerminated && (dataUpdate.aborted_at || dataUpdate.aborted_reason) && (
          <Alert variant={status === 'timeout' ? 'warning' : 'danger'} className="mb-3">
            <strong>{status === 'timeout' ? 'Aborted before completing' : 'Run errored'}</strong>
            {dataUpdate.aborted_at && (
              <div>
                <span className="text-muted">at:</span>{' '}
                <code>{dataUpdate.aborted_at}</code>
              </div>
            )}
            {dataUpdate.aborted_reason && (
              <div>
                <span className="text-muted">reason:</span> {dataUpdate.aborted_reason}
              </div>
            )}
          </Alert>
        )}

        {!data && (
          <Alert variant="info" className="mb-0">
            No performance breakdown available for this run.
            {' '}It either ran before per-step timings were added,
            or returned the legacy response shape.
          </Alert>
        )}

        {data?.rows?.length > 0 && (
          <div className="mb-4">
            <h6 className="mb-2">Rows</h6>
            <Table bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th className="text-end" style={{ width: '8rem' }}>Rows</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  // Labels can repeat, so the label alone isn't a safe key.
                  <tr key={`${row.label}-${i}`}>
                    <td>
                      {row.label}
                      {row.detail && (
                        <div className="text-muted small">{row.detail}</div>
                      )}
                    </td>
                    <td className="text-end">{formatNumber(row.count)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {data?.steps?.length > 0 && (
          <div className="mb-4">
            <h6 className="mb-2">Steps</h6>
            <Table bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Step</th>
                  <th className="text-end" style={{ width: '8rem' }}>Seconds</th>
                  {stepsHaveStatus && <th style={{ width: '8rem' }}>Status</th>}
                </tr>
              </thead>
              <tbody>
                {data.steps.map((step, i) => (
                  // Orchestrator runs can repeat a step name (e.g. the same
                  // importer for two files), so the name alone isn't a safe key.
                  <tr key={`${step.step}-${i}`}>
                    <td>{step.step}</td>
                    <td className="text-end">{formatSeconds(step.seconds)}</td>
                    {stepsHaveStatus && (
                      <td>{step.status != null ? <StatusBadge status={step.status} /> : '—'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {data?.analytics?.length > 0 && (
          <div>
            <h6 className="mb-2">Analytics ({data.analytics.length})</h6>
            <Table bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Analytic</th>
                  <th className="text-end" style={{ width: '7rem' }}>Seconds</th>
                  <th className="text-end" style={{ width: '8rem' }}>Rows in</th>
                  <th className="text-end" style={{ width: '8rem' }}>Rows out</th>
                  <th style={{ width: '8rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.analytics.map((a) => (
                  <tr key={`${a.module || ''}-${a.analytic_key}`}>
                    <td>
                      <code>{a.analytic_key}</code>
                      {a.module && (
                        <small className="text-muted ms-2">{a.module}</small>
                      )}
                    </td>
                    <td className="text-end">{formatSeconds(a.seconds)}</td>
                    <td className="text-end">{formatNumber(a.rows_in)}</td>
                    <td className="text-end">{formatNumber(a.rows_out)}</td>
                    <td><AnalyticStatusBadge status={a.status} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {data && (
          <details className="mt-4">
            <summary className="text-muted small">Raw response payload</summary>
            <pre className="bg-light p-2 rounded mt-2 small mb-0">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" className="text-primary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PerformancePayloadModal;
